import type { Split } from '#types/split.type';

import { isNotDefined } from '#misc/is-not-defined.guard';
import { isNotEmpty } from '#misc/is-not-empty';

import type { SplitOptions } from './types/split.options';

/**
 * Splits a string into an array of substrings based on a specified separator.
 * Optionally deduplicates the results, limits the number of items, and removes empty entries.
 *
 * @param {string} input - The string to split.
 * @param {SplitOptions} [options] - Optional configuration for splitting.
 * @returns {string[]} An array of substrings after applying the specified options.
 * @throws {Error} If the separator is not defined.
 */
export const split = <
  TString extends string,
  TDelimiter extends string = ',',
  TDedupe extends boolean = true,
  TRemoveEmpty extends boolean = true,
  TLimit extends number | undefined = undefined,
>(
  input: TString,
  options?: SplitOptions<TDelimiter, TDedupe, TRemoveEmpty, TLimit>
) => {
  const { dedupe, limit, removeEmptyEntries, separator } = {
    dedupe: true as TDedupe,
    removeEmptyEntries: true as TRemoveEmpty,
    separator: ',' as TDelimiter,
    ...options,
  };

  if (isNotDefined(separator)) {
    throw new Error('Separator is required');
  }

  const items = input.split(separator, limit).filter((item) => {
    return removeEmptyEntries ? isNotEmpty(item) : item;
  }) as Split<TString, TDelimiter, TDedupe, TRemoveEmpty, TLimit>;

  return dedupe ? [...new Set(items)] : items;
};
